import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import StarRadarChart from '../components/StarRadarChart';
import { adaptStarScoresForCompletion } from '../logic/insights/starAdapters';

type DataScope = 'consultant' | 'enterprise';

interface DashboardPageProps {
  scope?: DataScope;
}

interface Consultant {
  id: string;
  name: string;
  profilePicture?: string;
  email: string;
}

const ConsultantDashboardPage: React.FC<DashboardPageProps> = ({ scope = 'consultant' }) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [selectedTimeframe, setSelectedTimeframe] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [allUsers, setAllUsers] = useState<number>(0);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [totalAssessments, setTotalAssessments] = useState<number>(0);
  const [isLoadingAssessments, setIsLoadingAssessments] = useState(true);
  const [overallSowbScore, setOverallSowbScore] = useState<number>(0);
  const [isLoadingSowbScore, setIsLoadingSowbScore] = useState(true);
  const [scoreTrends, setScoreTrends] = useState<any[]>([]);
  const [isLoadingTrends, setIsLoadingTrends] = useState(true);
  const [weightedIndex, setWeightedIndex] = useState<number>(0);
  const [isLoadingWeightedIndex, setIsLoadingWeightedIndex] = useState(true);
  const [cohortType, setCohortType] = useState<'enterprise' | 'local'>('enterprise');
  const [radarAverages, setRadarAverages] = useState<{ individualAttributes: Record<string, number>; userCount: number } | null>(null);
  const [isLoadingRadar, setIsLoadingRadar] = useState(true);

  // Check if current user is admin
  const isAdmin = user?.role?.toLowerCase() === 'admin';




  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Refresh user and assessment data
    await Promise.all([
      fetchAllUsersCount(),
      fetchTotalAssessments(),
      fetchOverallSowbScore(),
      fetchWeightedIndex(),
      fetchScoreTrends(),
      fetchStarRadarAverages()
    ]);
    // Simulate additional refresh time for other metrics
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const fetchAllUsersCount = async () => {
    try {
      setIsLoadingUsers(true);
      const token = localStorage.getItem('token');
      if (!token) {

        return;
      }

      // Build query params
      const params = new URLSearchParams({
        limit: '1',
        page: '1',
        status: 'all',
        scope: 'cohort'
      });

      const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_ALL}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to fetch users count: ${response.status} ${response.statusText}`);
      }

      const fullResponse = await response.json();
      const data = fullResponse.data;

      // Set the total count from pagination
      if (data?.pagination?.totalUsers !== undefined) {
        setAllUsers(data.pagination.totalUsers);
      } else {

        // Fallback: try to use the users array length if available
        if (data?.users && Array.isArray(data.users)) {
          setAllUsers(data.users.length);
        } else {

        }
      }
    } catch (error) {

      // Keep the default value if there's an error
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchTotalAssessments = async () => {
    try {
      setIsLoadingAssessments(true);
      const token = localStorage.getItem('token');
      if (!token) {

        return;
      }

      // Build query params
      const params = new URLSearchParams({
        scope: 'cohort'
      });

      const response = await fetch(`${API_CONFIG.ENDPOINTS.ASSESSMENTS_ADMIN_TOTAL_COUNT}${params.toString() ? '?' + params.toString() : ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to fetch assessments count: ${response.status} ${response.statusText}`);
      }

      const fullResponse = await response.json();
      const data = fullResponse.data;

      // Set the total count from the response
      if (data?.totalAssessments !== undefined) {
        setTotalAssessments(data.totalAssessments);
      } else {

      }
    } catch (error) {

      // Keep the default value if there's an error
    } finally {
      setIsLoadingAssessments(false);
    }
  };

  const fetchOverallSowbScore = async () => {
    try {
      setIsLoadingSowbScore(true);
      const token = localStorage.getItem('token');
      if (!token) {

        return;
      }

      // Build query params
      const params = new URLSearchParams({
        scope: 'cohort'
      });

      const response = await fetch(`${API_CONFIG.ENDPOINTS.ASSESSMENTS_ADMIN_OVERALL_SOWB_SCORE}${params.toString() ? '?' + params.toString() : ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to fetch SoWB score: ${response.status} ${response.statusText}`);
      }

      const fullResponse = await response.json();
      const data = fullResponse.data;

      // Set the overall score from the response
      if (data?.overallScore !== undefined) {
        setOverallSowbScore(data.overallScore);
      } else {

      }
    } catch (error) {

      // Keep the default value if there's an error
    } finally {
      setIsLoadingSowbScore(false);
    }
  };

  const fetchWeightedIndex = async () => {
    try {
      setIsLoadingWeightedIndex(true);
      const token = localStorage.getItem('token');
      if (!token) {

        return;
      }

      // Build query params
      const params = new URLSearchParams({
        scope: 'cohort'
      });

      const response = await fetch(`${API_CONFIG.ENDPOINTS.ASSESSMENTS_ADMIN_WEIGHTED_WELLBEING_INDEX}${params.toString() ? '?' + params.toString() : ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to fetch weighted index: ${response.status} ${response.statusText}`);
      }

      const fullResponse = await response.json();
      const data = fullResponse.data;

      // Set the weighted index from the response
      if (data?.weightedIndex !== undefined) {
        setWeightedIndex(data.weightedIndex);
      } else {

      }
    } catch (error) {

      // Keep the default value if there's an error
    } finally {
      setIsLoadingWeightedIndex(false);
    }
  };

  const fetchScoreTrends = async () => {
    try {
      setIsLoadingTrends(true);
      const token = localStorage.getItem('token');
      if (!token) {

        return;
      }

      // Build query params
      const params = new URLSearchParams({
        timeframe: selectedTimeframe,
        scope: 'cohort'
      });

      const response = await fetch(`${API_CONFIG.ENDPOINTS.ASSESSMENTS_ADMIN_SCORE_TRENDS}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to fetch score trends: ${response.status} ${response.statusText}`);
      }

      const fullResponse = await response.json();
      const data = fullResponse.data;

      // Set the trends data from the response
      if (data?.trends) {
        setScoreTrends(data.trends);
      } else {

        setScoreTrends([]);
      }
    } catch (error) {

      // Keep the default value if there's an error
      setScoreTrends([]);
    } finally {
      setIsLoadingTrends(false);
    }
  };

  const fetchStarRadarAverages = async () => {
    try {
      setIsLoadingRadar(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const params = new URLSearchParams({
        timeframe: selectedTimeframe,
        scope: 'cohort',
        cohortType
      });

      const response = await fetch(`${API_CONFIG.ENDPOINTS.ASSESSMENTS_ADMIN_STAR_RADAR_AVERAGES}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        setRadarAverages(null);
        return;
      }

      const fullResponse = await response.json();
      const data = fullResponse.data;
      if (data?.individualAttributes !== undefined) {
        setRadarAverages({
          individualAttributes: data.individualAttributes,
          userCount: data.userCount ?? 0
        });
      } else {
        setRadarAverages(null);
      }
    } catch (error) {
      console.error('Error fetching star radar averages:', error);
      setRadarAverages(null);
    } finally {
      setIsLoadingRadar(false);
    }
  };

  useEffect(() => {
    fetchAllUsersCount();
    fetchTotalAssessments();
    fetchOverallSowbScore();
    fetchWeightedIndex();
    fetchScoreTrends();
  }, []);

  // Fetch trends when timeframe changes
  useEffect(() => {
    fetchScoreTrends();
  }, [selectedTimeframe]);

  // Fetch radar averages when timeframe or cohort type changes
  useEffect(() => {
    fetchStarRadarAverages();
  }, [selectedTimeframe, cohortType]);

  // Transform score trends data for the chart
  const chartData = scoreTrends.map((trend, index) => ({
    week: trend.label,
    percentage: trend.score,
    isCurrent: index === scoreTrends.length - 1
  }));

  const activeUserData = [
    { height: '30%' },
    { height: '50%' },
    { height: '40%' },
    { height: '60%' },
    { height: '80%' },
    { height: '55%' },
    { height: '70%' },
    { height: '95%', isCurrent: true }
  ];

  return (
    <div className={`consultant-dashboard ${isDarkMode ? 'dark' : ''}`}>
      <div className="consultant-dashboard__container">
        <main className="consultant-dashboard__main">
          <div className="consultant-dashboard__content">
            {/* Header Section */}
            <header className="consultant-dashboard__header">
              <h1 className="consultant-dashboard__title">
                SoWB Overall Well-being Score Report
              </h1>
              <p className="consultant-dashboard__description">
                Overview of key health metrics, engagement statistics, and weighted indices for the current cohort.
              </p>
            </header>

            {/* Controls Section */}
            <div className="consultant-dashboard__controls">

              {/* Timeframe Selector */}
              <div className="consultant-dashboard__timeframe-selector">
                <label className="consultant-dashboard__timeframe-option">
                  <input
                    type="radio"
                    name="timeframe"
                    value="7d"
                    checked={selectedTimeframe === '7d'}
                    onChange={(e) => setSelectedTimeframe(e.target.value as '7d')}
                    className="consultant-dashboard__timeframe-input"
                  />
                  <span className="consultant-dashboard__timeframe-label">7 Days</span>
                </label>
                <label className="consultant-dashboard__timeframe-option">
                  <input
                    type="radio"
                    name="timeframe"
                    value="30d"
                    checked={selectedTimeframe === '30d'}
                    onChange={(e) => setSelectedTimeframe(e.target.value as '30d')}
                    className="consultant-dashboard__timeframe-input"
                  />
                  <span className="consultant-dashboard__timeframe-label">30 Days</span>
                </label>
                <label className="consultant-dashboard__timeframe-option">
                  <input
                    type="radio"
                    name="timeframe"
                    value="90d"
                    checked={selectedTimeframe === '90d'}
                    onChange={(e) => setSelectedTimeframe(e.target.value as '90d')}
                    className="consultant-dashboard__timeframe-input"
                  />
                  <span className="consultant-dashboard__timeframe-label">90 Days</span>
                </label>
                <label className="consultant-dashboard__timeframe-option">
                  <input
                    type="radio"
                    name="timeframe"
                    value="all"
                    checked={selectedTimeframe === 'all'}
                    onChange={(e) => setSelectedTimeframe(e.target.value as 'all')}
                    className="consultant-dashboard__timeframe-input"
                  />
                  <span className="consultant-dashboard__timeframe-label">All Time</span>
                </label>
              </div>

              {/* Refresh Button */}
              <button
                className="consultant-dashboard__refresh-btn"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <span className="material-symbols-outlined consultant-dashboard__refresh-icon">
                  sync
                </span>
                Refresh data
              </button>
            </div>


            {/* Metrics Cards */}
            <div className="consultant-dashboard__metrics">
              {/* Overall SoWB Score Card */}
              <div className="consultant-dashboard__metric-card consultant-dashboard__metric-card--primary">
                <div className="consultant-dashboard__metric-header">
                  <div className="consultant-dashboard__metric-info">
                    <p className="consultant-dashboard__metric-label">Overall SoWB Score</p>
                    <h3 className="consultant-dashboard__metric-value">
                      {isLoadingSowbScore ? '...' : overallSowbScore.toFixed(1)}
                    </h3>
                  </div>
                  <div className="consultant-dashboard__metric-icon">
                    <span className="material-symbols-outlined">trending_up</span>
                  </div>
                </div>
                <div className="consultant-dashboard__metric-progress">
                  <div
                    className="consultant-dashboard__metric-progress-bar"
                    style={{ width: `${overallSowbScore}%` }}
                  ></div>
                </div>
                <div className="consultant-dashboard__metric-trend">
                  <span className="consultant-dashboard__metric-trend-value">
                    <span className="material-symbols-outlined">arrow_upward</span>
                    2.1%
                  </span>
                  <span className="consultant-dashboard__metric-trend-label">vs last period</span>
                </div>
              </div>

              {/* Weighted Index Card */}
              <div className="consultant-dashboard__metric-card consultant-dashboard__metric-card--secondary">
                <div className="consultant-dashboard__metric-header">
                  <div className="consultant-dashboard__metric-info">
                    <p className="consultant-dashboard__metric-label">Weighted Index (WWi)</p>
                    <h3 className="consultant-dashboard__metric-value">
                      {isLoadingWeightedIndex ? '...' : weightedIndex.toFixed(1)}
                    </h3>
                  </div>
                  <div className="consultant-dashboard__metric-icon">
                    <span className="material-symbols-outlined">balance</span>
                  </div>
                </div>
                <div className="consultant-dashboard__metric-scale">
                  <div className="consultant-dashboard__metric-scale-bar">
                    <div className="consultant-dashboard__metric-scale-segment"></div>
                    <div className="consultant-dashboard__metric-scale-segment"></div>
                    <div className="consultant-dashboard__metric-scale-segment consultant-dashboard__metric-scale-segment--active"></div>
                    <div className="consultant-dashboard__metric-scale-segment"></div>
                    <div className="consultant-dashboard__metric-scale-segment"></div>
                  </div>
                </div>
                <div className="consultant-dashboard__metric-trend">
                  <span className="consultant-dashboard__metric-trend-label consultant-dashboard__metric-trend-label--scale">Scale: 1-10</span>
                  <span className="consultant-dashboard__metric-trend-value">
                    <span className="material-symbols-outlined">arrow_upward</span>
                    0.4
                  </span>
                </div>
              </div>

              {/* Total Assessments Card */}
              <div className="consultant-dashboard__metric-card consultant-dashboard__metric-card--tertiary">
                <div className="consultant-dashboard__metric-header">
                  <div className="consultant-dashboard__metric-info">
                    <p className="consultant-dashboard__metric-label">Total Assessments</p>
                    <h3 className="consultant-dashboard__metric-value">
                      {isLoadingAssessments ? '...' : totalAssessments.toLocaleString()}
                    </h3>
                  </div>
                  <div className="consultant-dashboard__metric-icon">
                    <span className="material-symbols-outlined">assignment_turned_in</span>
                  </div>
                </div>
                <div className="consultant-dashboard__metric-trend">
                  <span className="consultant-dashboard__metric-trend-label">Completion rate</span>
                  <span className="consultant-dashboard__metric-completion-rate">92%</span>
                </div>
              </div>

              {/* Active Users Card */}
              <div className="consultant-dashboard__metric-card consultant-dashboard__metric-card--quaternary">
                <div className="consultant-dashboard__metric-header">
                  <div className="consultant-dashboard__metric-info">
                    <p className="consultant-dashboard__metric-label">All Users</p>
                    <h3 className="consultant-dashboard__metric-value">
                      {isLoadingUsers ? '...' : allUsers.toLocaleString()}
                    </h3>
                  </div>
                  <div className="consultant-dashboard__metric-icon">
                    <span className="material-symbols-outlined">group</span>
                  </div>
                </div>
                <div className="consultant-dashboard__metric-activity-chart">
                  {activeUserData.map((bar, index) => (
                    <div
                      key={index}
                      className={`consultant-dashboard__metric-activity-bar ${bar.isCurrent ? 'consultant-dashboard__metric-activity-bar--current' : ''}`}
                      style={{ height: bar.height }}
                    ></div>
                  ))}
                </div>
                <div className="consultant-dashboard__metric-trend">
                  <span className="consultant-dashboard__metric-trend-value">
                    <span className="material-symbols-outlined">arrow_upward</span>
                    12%
                  </span>
                  <span className="consultant-dashboard__metric-trend-label">growth</span>
                </div>
              </div>
            </div>

            <div className="consultant-dashboard__charts-grid">
            {/* Chart Section */}
            <div className="consultant-dashboard__chart-section">
              <div className="consultant-dashboard__chart-header">
                <div className="consultant-dashboard__chart-info">
                  <h3 className="consultant-dashboard__chart-title">Score Trends Overview</h3>
                  <p className="consultant-dashboard__chart-description">
                    {selectedTimeframe === '7d' && 'Daily progression of overall well-being score (7 days)'}
                    {selectedTimeframe === '30d' && 'Daily progression of overall well-being score (30 days)'}
                    {selectedTimeframe === '90d' && 'Weekly progression of overall well-being score (90 days)'}
                    {selectedTimeframe === 'all' && 'Monthly progression of overall well-being score (All time)'}
                  </p>
                </div>
                <button className="consultant-dashboard__chart-btn">
                  View detailed report
                  <span className="material-symbols-outlined">arrow_forward</span>
                </button>
              </div>

              <div className="consultant-dashboard__chart-container">
                {isLoadingTrends ? (
                  <div className="consultant-dashboard__chart-loading">
                    <span>Loading trends data...</span>
                  </div>
                ) : scoreTrends.length === 0 ? (
                  <div className="consultant-dashboard__chart-empty">
                    <span>No data available for the selected timeframe</span>
                  </div>
                ) : (
                  <>
                    <div className="consultant-dashboard__chart-bars">
                      {chartData.map((data, index) => (
                        <div
                          key={data.week}
                          className={`consultant-dashboard__chart-bar ${data.isCurrent ? 'consultant-dashboard__chart-bar--current' : ''}`}
                          style={{ height: `${data.percentage}%` }}
                        >
                          <div className="consultant-dashboard__chart-tooltip">
                            {data.percentage}%
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="consultant-dashboard__chart-labels">
                      {chartData.map((data) => (
                        <span
                          key={data.week}
                          className={`consultant-dashboard__chart-label ${data.isCurrent ? 'consultant-dashboard__chart-label--current' : ''}`}
                        >
                          {data.isCurrent ? 'Current' : data.week.split(' ').pop()}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Population radar (10 dimensions) - cohort average */}
            <div className="consultant-dashboard__radar-section">
              <h3 className="consultant-dashboard__radar-title">Star of Wellbeing Constellation</h3>
              <p className="consultant-dashboard__radar-subtitle">Population radar across 10 dimensions</p>
              {isLoadingRadar ? (
                <div className="consultant-dashboard__radar-loading">
                  <span>Loading radar data...</span>
                </div>
              ) : !radarAverages || radarAverages.userCount === 0 ? (
                <div className="consultant-dashboard__radar-empty">
                  <span>No cohort data yet</span>
                </div>
              ) : (
                <div className="consultant-dashboard__radar-chart-wrap">
                  <StarRadarChart
                    scores={adaptStarScoresForCompletion(radarAverages.individualAttributes)}
                    chartMode="filled"
                    size="full"
                    labelFontSize={26}
                  />
                </div>
              )}
              <div className="consultant-dashboard__cohort-selector">
                <label className="consultant-dashboard__cohort-option">
                  <input
                    type="radio"
                    name="cohortType"
                    value="enterprise"
                    checked={cohortType === 'enterprise'}
                    onChange={() => setCohortType('enterprise')}
                    className="consultant-dashboard__cohort-input"
                  />
                  <span className="consultant-dashboard__cohort-label">Enterprise assigned</span>
                </label>
                <label className="consultant-dashboard__cohort-option">
                  <input
                    type="radio"
                    name="cohortType"
                    value="local"
                    checked={cohortType === 'local'}
                    onChange={() => setCohortType('local')}
                    className="consultant-dashboard__cohort-input"
                  />
                  <span className="consultant-dashboard__cohort-label">Locally assigned</span>
                </label>
              </div>
            </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ConsultantDashboardPage;

